// src/lib/resa-prorata-splits.js — Lot 5b PRORATA (garde-fous de POSE du calque).
//
// ⚠️ DUPLICATION CONTRÔLÉE — Pattern ADR-021.
// Ce fichier est la version ESM ; le miroir CJS est lib/resa-prorata-splits.js.
// Toute modification DOIT être appliquée AUX DEUX fichiers (test de parité
// resa-prorata-splits-cjs-parity.test.js — verrou anti-dérive).
//
// CHEMIN ARGENT — ces helpers sont les GARDE-FOUS de POSE du store
// data._prorataSplits[lid][code] (le calque résolu par prorata-resolve.js, 5a).
// Ils valident AVANT écriture : code stable, mois touchés non vides, AUCUN mois
// gelé (facturé), XOR avec le déplacement ◀▶, et — en mode manual — la cohérence
// stricte Σ(amounts) == montantTotal POST-OVERRIDE / PRÉ-PRORATA. FAIL-CLOSED :
// tout doute (montantTotal non-fini, mois fantôme, montant négatif) → refus, on
// ne pose pas le calque (la résa reste ENTIÈRE sur son mois naturel).
//
// Helpers PURS (testables) : AUCUNE dépendance Supabase/React. Le gel passe par
// les findBlocking* (série P commission + série V voyageur) injectés depuis
// invoice-guard, et le LIBELLÉ FR via le monthKeyToLabel REÇU en paramètre
// (jamais reconstruit ici — il doit matcher meta.mois STRICT).
//
// MODÈLE DE DONNÉES (data._prorataSplits[lid][code]) :
//   { mode:'auto', excluded:['2026-09', ...] }
//   { mode:'manual', amounts:{'2026-06':800, ...} }

import { nightsInMonth, addDaysISO, addMonthsMk } from './resa-invoice-month'
import {
  findBlockingCommission,
  findBlockingVoyageurInvoice,
  findBlockingVoyageurInvoiceForMonth,
} from './invoice-guard'

// round2 : arrondi 2 décimales (centime). Source unique du module.
function round2(n) {
  return Math.round(n * 100) / 100
}

// ── computeMonthsTouched(resa) → string[] ──
// CORRECTION CHEMIN ARGENT — version DATE-BOUNDED self-contained : le périmètre
// temporel d'un prorata = TOUS les mois RÉELLEMENT occupés par le séjour
// (check-in → dernière nuit), JAMAIS la fenêtre de projection. Un mois occupé
// hors fenêtre (ex. septembre d'une résa 20/06→11/09 quand la fenêtre s'arrête
// en août) DOIT figurer ici, sinon sa part fond dans le résidu du mois voisin.
//
// Itère du mois du check-in jusqu'au mois de la DERNIÈRE NUIT (end exclu →
// addDaysISO(end,-1)). Retient un mk SSI nightsInMonth(resa, mk) > 0 (UTC pur,
// fin dérivée de r.nuits si checkOut absent — même prédicat que le moteur).
//
// 2e argument historique (monthKeys) RETIRÉ : la fenêtre n'est plus un intrant.
export function computeMonthsTouched(resa) {
  if (!resa) return []
  const checkIn = resa.checkIn || resa.date
  const start = addDaysISO(checkIn, 0) // valide la date ; null si invalide
  if (!start) return []
  let end = resa.checkOut ? addDaysISO(resa.checkOut, 0) : null
  if (!end || end <= start) {
    const n = Number(resa.nuits)
    end = addDaysISO(start, Number.isFinite(n) && n > 0 ? n : 1)
  }
  if (!end) return []
  const out = []
  let mk = start.slice(0, 7)
  const last = addDaysISO(end, -1) // dernière NUIT (end exclu) → dernier mois occupé
  if (!last) return []
  const endMk = last.slice(0, 7)
  while (mk && mk <= endMk) {
    if (nightsInMonth(resa, mk) > 0) out.push(mk)
    mk = addMonthsMk(mk, 1)
  }
  return out
}

// ── assertProrataPosable({...}) → { ok:true } | { ok:false, reason, mk?, message } ──
// Valide qu'on PEUT poser le calque prorata `spec` sur (resa, code). FAIL-CLOSED.
//
// @param {object} resa — résa NATURELLE (pré-prorata), pour nightsInMonth en
//   mode manual (cohérence des bornes mois). Source = projectedByCode côté moteur.
// @param {string|null} code — code stable (airbnbCode || bookingRef).
// @param {string[]} monthsTouched — mois touchés (computeMonthsTouched, MÊME
//   fenêtre que le moteur).
// @param {number} montantTotal — total POST-OVERRIDE / PRÉ-PRORATA (B1) — REÇU,
//   jamais re-dérivé de resa.montant.
// @param {object} spec — { mode:'auto', excluded?:string[] }
//                      | { mode:'manual', amounts:{[mk]:number} }.
// @param {Array} invoicesRaw — lignes BRUTES table invoices (gel série P + V).
// @param {string} logId — id immuable du logement (jamais le nom).
// @param {function} monthKeyToLabel — mk → libellé FR « Juillet 2026 » (REÇU ;
//   doit matcher meta.mois STRICT — JAMAIS reconstruit ici).
// @param {*} existingMonthOverride — data._monthOverrides[lid][code] (XOR move).
// reasons : 'no_code' | 'no_months' | 'frozen' | 'xor_move'
//         | 'manual_sum' | 'manual_negative' | 'manual_phantom'.
export function assertProrataPosable({
  resa,
  code,
  monthsTouched,
  montantTotal,
  spec,
  invoicesRaw,
  logId,
  monthKeyToLabel,
  existingMonthOverride,
}) {
  // 1. Code stable requis (sinon le store data._prorataSplits[lid][code] n'a pas
  //    de clé fiable — un renommage casserait l'historique).
  if (!code) {
    return {
      ok: false,
      reason: 'no_code',
      message: 'Réservation non proratisable (pas de code stable).',
    }
  }

  // 2. Au moins un mois touché (sinon rien à répartir).
  if (!Array.isArray(monthsTouched) || monthsTouched.length === 0) {
    return {
      ok: false,
      reason: 'no_months',
      message: 'Réservation non proratisable (aucun mois touché).',
    }
  }

  // 3. AMENDEMENT M2 — GEL : pour CHAQUE mois touché, refuser si une facture
  //    commission P (proprio) OU voyageur V par MOIS est déjà émise ; refuser
  //    aussi si la résa a une facture voyageur V par reservationId. Un mois
  //    facturé ne peut PAS reperdre/recomposer sa part. Libellé via le
  //    monthKeyToLabel REÇU (matche meta.mois STRICT — jamais reconstruit).
  if (Array.isArray(invoicesRaw)) {
    // Gel par résa (série V) — indépendant des mois.
    if (
      resa &&
      resa.id != null &&
      findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa.id })
    ) {
      return {
        ok: false,
        reason: 'frozen',
        message: 'Impossible de proratiser : cette réservation est déjà facturée.',
      }
    }
    for (const mk of monthsTouched) {
      const label = typeof monthKeyToLabel === 'function' ? monthKeyToLabel(mk) : null
      if (!label) continue
      if (
        findBlockingCommission(invoicesRaw, { logId, monthLabel: label }) ||
        findBlockingVoyageurInvoiceForMonth(invoicesRaw, { logId, monthLabel: label })
      ) {
        return {
          ok: false,
          reason: 'frozen',
          mk,
          message: `Impossible de proratiser : le mois ${label} est déjà facturé.`,
        }
      }
    }
  }

  // 4. XOR move : une résa DÉPLACÉE (◀▶) ne peut pas être proratisée
  //    (sémantiques incompatibles — le moteur les rend mutuellement exclusives,
  //    cf. data-projection.js:627). Refus côté POSE pour cohérence.
  if (existingMonthOverride) {
    return {
      ok: false,
      reason: 'xor_move',
      message:
        'Cette réservation est déplacée (◀▶) : retirez le déplacement avant de proratiser.',
    }
  }

  // 5. Mode manual — AMENDEMENT B1 : validation Σ contre le BON total
  //    (montantTotal REÇU = post-override / pré-prorata, JAMAIS resa.montant).
  if (spec && spec.mode === 'manual') {
    // FAIL-CLOSED : sans total fiable, on ne peut pas valider la somme → refus.
    if (!Number.isFinite(montantTotal)) {
      return {
        ok: false,
        reason: 'manual_sum',
        message: 'Montant total indéterminable : proratisation manuelle refusée.',
      }
    }
    const amounts = spec.amounts
    if (!amounts || typeof amounts !== 'object') {
      return {
        ok: false,
        reason: 'manual_sum',
        message: 'Montants manuels manquants.',
      }
    }
    const touchedSet = new Set(monthsTouched)
    let sum = 0
    for (const mk of Object.keys(amounts)) {
      // Mois fantôme : un mk hors des mois touchés est incohérent.
      if (!touchedSet.has(mk)) {
        return {
          ok: false,
          reason: 'manual_phantom',
          mk,
          message: `Le mois ${typeof monthKeyToLabel === 'function' ? monthKeyToLabel(mk) : mk} ne fait pas partie du séjour.`,
        }
      }
      const a = Number(amounts[mk])
      // Montant non-fini OU négatif → refus (fail-closed).
      if (!Number.isFinite(a) || a < 0) {
        return {
          ok: false,
          reason: 'manual_negative',
          mk,
          message: `Le montant du mois ${typeof monthKeyToLabel === 'function' ? monthKeyToLabel(mk) : mk} doit être un nombre positif.`,
        }
      }
      sum = round2(sum + a)
    }
    sum = round2(sum)
    if (Math.abs(sum - montantTotal) > 0.01) {
      return {
        ok: false,
        reason: 'manual_sum',
        message: `La somme (${round2(sum)} €) doit égaler ${round2(montantTotal)} €.`,
      }
    }
    return { ok: true }
  }

  // 6. Mode auto (défaut) : pas de validation Σ (le moteur répartit aux nuits).
  //    OK si pas de gel ni de XOR move (déjà testés ci-dessus).
  return { ok: true }
}

// ── setProrataSplitStore(nd, lid, code, spec) ──
// Pose défensive du calque sur un deep-clone `nd` (clone géré par l'appelant,
// comme setMonthOverride). Garde : nd objet, lid + code requis. Miroir de
// setMonthOverride (resa-month-overrides.js).
export function setProrataSplitStore(nd, lid, code, spec) {
  if (!nd || typeof nd !== 'object') return
  if (!lid || !code) return
  if (!spec || typeof spec !== 'object') return
  if (!nd._prorataSplits) nd._prorataSplits = {}
  if (!nd._prorataSplits[lid] || typeof nd._prorataSplits[lid] !== 'object') {
    nd._prorataSplits[lid] = {}
  }
  nd._prorataSplits[lid][code] = spec
}

// ── removeProrataSplitStore(nd, lid, code) ──
// Retire data._prorataSplits[lid][code] (retour au mois naturel) + cleanup du
// lid vide PUIS du store vide. No-op défensif si absent. Miroir de
// removeMonthOverride.
export function removeProrataSplitStore(nd, lid, code) {
  if (!nd || typeof nd !== 'object') return
  if (!lid || !code) return
  const store = nd._prorataSplits
  if (!store || typeof store !== 'object') return
  const lidMap = store[lid]
  if (!lidMap || typeof lidMap !== 'object') return
  if (Object.prototype.hasOwnProperty.call(lidMap, code)) {
    delete lidMap[code]
    if (Object.keys(lidMap).length === 0) delete store[lid]
  }
  if (Object.keys(store).length === 0) delete nd._prorataSplits
}

export { round2 }
